import { Button, Group, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { browserPage } from '@/app/staleChunk';
import { runningVersion, useNewVersionAvailable } from '@/app/versionCheck';

/**
 * "Hay una versión nueva de ServiceLedger." with "Actualizar": another version was published while
 * this tab was open (`versionCheck.ts`). The reload is the person's call, so nothing they are
 * typing is lost; the bar stays until they take it, with no way to dismiss it, because the tab
 * would otherwise reload by itself at the first page it cannot find (`staleChunk.ts`). Mounted
 * once in each shell, above the content and in its flow, so on a phone it pushes the content down
 * instead of covering it.
 *
 * The polite live region (`role="status"`) is in the page from the start, empty and with no height,
 * and the bar is written into it: a screen reader announces a change inside a region it already
 * knows, not reliably one that enters the page with its text. A build that never checks (`pnpm
 * dev`, the tests that do not turn it on) has nothing to announce and leaves the region out.
 */
export function NewVersionBar() {
  const { t } = useTranslation('common');
  const available = useNewVersionAvailable();

  if (!runningVersion.checks) {
    return null;
  }

  return (
    <div role="status">
      {available ? (
        <Group
          gap="sm"
          justify="center"
          py={6}
          px="sm"
          wrap="wrap"
          style={{
            background: 'var(--mantine-color-steel-1)',
            borderBottom: '1px solid var(--mantine-color-steel-3)',
          }}
        >
          <Text size="sm" fw={600} c="steel.8">
            {t('newVersion.banner')}
          </Text>
          <Button size="compact-xs" onClick={() => browserPage.reload()}>
            {t('newVersion.update')}
          </Button>
        </Group>
      ) : null}
    </div>
  );
}
